import { useState, useMemo, useRef, useEffect } from "react";
import { CSSTransition } from "react-transition-group";
import { ChevronLeft, ChevronRight, Clock, Phone, MapPin, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDragScroll } from "../../hooks/useDragScroll";

const MEETING_TYPE_ICONS = {
  phone: Phone,
  in_person: MapPin,
};

export default function SlotPicker({
  slots,
  onSelect,
  selectedSlot,
  selectedMeetingType,
  onSelectMeetingType,
}) {
  const { t, i18n } = useTranslation();
  const [userSelectedDate, setUserSelectedDate] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const selectedButtonRef = useRef(null);
  const dropdownRef = useRef(null);
  const { ref: dragScrollRef } = useDragScroll();

  const scrollContainerRef = useRef(null);
  const isAutoScrollingRef = useRef(false);

  // Update dropdown position when selection changes, with auto-scroll
  useEffect(() => {
    if (selectedSlot && selectedButtonRef.current) {
      const button = selectedButtonRef.current;
      const container = scrollContainerRef.current;

      // Auto-scroll the button into view if partially hidden
      if (container) {
        const buttonRect = button.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // Check if button is partially outside the visible area
        if (buttonRect.bottom > containerRect.bottom || buttonRect.top < containerRect.top) {
          isAutoScrollingRef.current = true;
          button.scrollIntoView({ behavior: 'smooth', block: 'center' });

          // Position dropdown after scroll completes
          setTimeout(() => {
            const newRect = button.getBoundingClientRect();
            setDropdownPosition({
              top: newRect.bottom + 4,
              left: newRect.left,
              width: newRect.width,
            });
            // Reset auto-scroll flag after animation
            setTimeout(() => {
              isAutoScrollingRef.current = false;
            }, 50);
          }, 200);
          return;
        }
      }

      // Position dropdown immediately if no scroll needed
      const rect = button.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [selectedSlot]);

  // Close dropdown on manual scroll (not auto-scroll)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !selectedSlot) return;

    const handleScroll = () => {
      // Only close if user is manually scrolling, not auto-scroll
      if (!isAutoScrollingRef.current) {
        setIsDropdownOpen(false);
        onSelect(null);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [selectedSlot, onSelect]);

  // Get translated meeting type label
  const getMeetingTypeLabel = (meetingType) => {
    const typeMap = { phone: 'phone', in_person: 'inPerson' };
    return t(`appointment.meetingType.${typeMap[meetingType] || 'phone'}`);
  };

  // Group slots by date
  const slotsByDate = useMemo(() => {
    const grouped = {};
    slots.forEach((slot) => {
      const date = slot.date;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(slot);
    });
    return grouped;
  }, [slots]);

  // Get unique dates
  const dates = useMemo(() => {
    return Object.keys(slotsByDate).sort();
  }, [slotsByDate]);

  // Derive effective selected date - use user selection or default to first date
  const selectedDate = userSelectedDate && dates.includes(userSelectedDate)
    ? userSelectedDate
    : dates[0] ?? null;

  const currentDateIndex = dates.indexOf(selectedDate);

  const goToPrevDate = () => {
    if (currentDateIndex > 0) {
      setUserSelectedDate(dates[currentDateIndex - 1]);
    }
  };

  const goToNextDate = () => {
    if (currentDateIndex < dates.length - 1) {
      setUserSelectedDate(dates[currentDateIndex + 1]);
    }
  };

  const locale = i18n.language === 'ko' ? 'ko-KR' : 'en-US';

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString(locale, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateLong = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString(locale, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  if (slots.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
        <p className="text-sm">
          {t('appointment.slots.noSlotsAvailable')}
        </p>
      </div>
    );
  }

  const currentSlots = selectedDate ? slotsByDate[selectedDate] || [] : [];

  return (
    <div className="space-y-4">

      <p className="text-xs font-semibold text-gray-600 dark:text-gray-400">
        {t('appointment.slots.timezoneNote')}
      </p>

      {/* Date Navigation */}
      <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
        <button
          onClick={goToPrevDate}
          disabled={currentDateIndex <= 0}
          className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        </button>

        <div className="text-center">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            {selectedDate ? formatDateLong(selectedDate) : t('appointment.slots.selectDate')}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('appointment.slots.slotsAvailable', { count: currentSlots.length })}
          </p>
        </div>

        <button
          onClick={goToNextDate}
          disabled={currentDateIndex >= dates.length - 1}
          className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        </button>
      </div>

      {/* Date Chips */}
      <div ref={dragScrollRef} className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide min-h-11">
        {dates.map((date) => (
          <button
            key={date}
            onClick={() => setUserSelectedDate(date)}
            className={`shrink-0 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedDate === date
                ? "bg-[#589DD5] text-white"
                : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
            }`}
          >
            {formatDate(date)}
          </button>
        ))}
      </div>

      {/* Time Slots - selected slot expands to show meeting type options */}
      <div ref={scrollContainerRef} className="h-60 overflow-y-auto pr-1">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 content-start">
          {currentSlots.map((slot) => {
            const isSelected =
              selectedSlot?.availability_id === slot.availability_id &&
              selectedSlot?.datetime_iso === slot.datetime_iso;
            // Use meeting_types array if available, otherwise fallback to single meeting_type
            const meetingTypes = slot.meeting_types || [slot.meeting_type];

            return (
              <button
                key={`${slot.availability_id}-${slot.datetime_iso}`}
                ref={isSelected ? selectedButtonRef : null}
                onClick={() => {
                  if (isSelected) {
                    // If dropdown is open, close and deselect; if closed, reopen it
                    if (isDropdownOpen) {
                      onSelect(null);
                    } else {
                      setIsDropdownOpen(true);
                    }
                  } else {
                    // Select new slot and open dropdown
                    onSelect(slot);
                    setIsDropdownOpen(true);
                  }
                }}
                className={`w-full flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-colors ${
                  isSelected
                    ? "border-[#589DD5] bg-[#589DD5]/10 ring-2 ring-[#589DD5]/30"
                    : "border-gray-200 dark:border-gray-700 hover:border-[#589DD5]/50 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <span
                  className={`text-sm font-semibold ${
                    isSelected ? "text-[#589DD5]" : "text-gray-900 dark:text-white"
                  }`}
                >
                  {slot.time_display}
                </span>
                <div className="flex items-center gap-1 mt-1 flex-wrap justify-center">
                  {meetingTypes.map((type) => {
                    const MeetingIcon = MEETING_TYPE_ICONS[type] || Phone;
                    const isTypeSelected = isSelected && selectedMeetingType === type;
                    return (
                      <div
                        key={type}
                        className={`w-5 h-5 flex items-center justify-center rounded-full transition-colors ${
                          isTypeSelected ? "bg-[#589DD5]" : ""
                        }`}
                        title={getMeetingTypeLabel(type)}
                      >
                        <MeetingIcon
                          className={`w-3 h-3 ${
                            isTypeSelected
                              ? "text-white"
                              : isSelected
                                ? "text-[#589DD5]"
                                : "text-gray-400 dark:text-gray-500"
                          }`}
                        />
                      </div>
                    );
                  })}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Meeting type dropdown - fixed position to escape scroll container */}
      {/* Key forces remount on slot change to prevent stale selectedMeetingType flicker */}
      <CSSTransition
        key={selectedSlot ? `${selectedSlot.availability_id}-${selectedSlot.datetime_iso}` : 'none'}
        in={isDropdownOpen && !!selectedSlot && !!dropdownPosition}
        timeout={200}
        classNames="dropdown-animate"
        unmountOnExit
        nodeRef={dropdownRef}
      >
        <div
          ref={dropdownRef}
          className="fixed z-50 bg-white dark:bg-gray-800 rounded-lg border border-[#589DD5]/30 shadow-lg p-1.5 space-y-1"
          style={{
            top: dropdownPosition?.top,
            left: dropdownPosition?.left,
            width: dropdownPosition?.width,
          }}
        >
          {selectedSlot && (selectedSlot.meeting_types || [selectedSlot.meeting_type]).map((type) => {
            const MeetingIcon = MEETING_TYPE_ICONS[type] || Phone;
            const isTypeSelected = selectedMeetingType === type;
            return (
              <button
                key={type}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectMeetingType?.(type);
                  setIsDropdownOpen(false);
                }}
                className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-left transition-colors ${
                  isTypeSelected
                    ? "bg-[#589DD5]/10 text-[#589DD5]"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                <div className="flex items-center gap-2">
                  <MeetingIcon className="w-3.5 h-3.5 shrink-0" />
                  <span className="text-xs font-medium truncate">
                    {getMeetingTypeLabel(type)}
                  </span>
                </div>
                {isTypeSelected && (
                  <div className="w-4 h-4 rounded-full bg-[#589DD5] flex items-center justify-center shrink-0">
                    <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </CSSTransition>
    </div>
  );
}
